import styled from "styled-components";
import { SearchProduct } from "../api/models/searchProduct.models";
import { useShallow } from "zustand/shallow";
import useModelWithIdentifier from "../hooks/useModelWithIdentifier";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import { HttpRequestStatus, LoadingContainer } from "./loadingContainer";
import {
  DataTable,
  DataTableExpandedRows,
  DataTableValueArray,
} from "primereact/datatable";
import { Column } from "primereact/column";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { TypeTag } from "./typeTag";
import useAssociatedAbstractModels from "../hooks/useAssociatedAbstractProducts";
import { Family } from "../api/models/fullProductData.models";
import { combineKeys } from "../utils/akeneoUtils";

const StyledTable = styled(DataTable)`
  .p-datatable-row-expansion .p-datatable-tbody > tr > td {
    background-color: #f9f9f9 !important;
  }
`;

const ExpandedContainer = styled.div`
  padding: 0rem;
`;

const IdentifierLink = styled.div`
  color: var(--text-color);
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    color: var(--text-color-secondary);
    text-decoration: underline;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--sumi-blue);
  }
`;

interface Props {
  searchProduct: SearchProduct;
  handleSelectItem: (model: SearchProduct) => void;
}

const SearchProductExpanded: React.FC<Props> = ({
  searchProduct,
  handleSelectItem,
}) => {
  const { t } = useTranslation();
  const [companyId] = useUIStore(
    useShallow((state: UIState) => [state.companyId]),
  );
  const [expandedRows, setExpandedRows] = useState<
    DataTableExpandedRows | DataTableValueArray | undefined
  >(undefined);
  const [tableData, setTableData] = useState<any[]>([]);

  const { modelData, isLoadingModel } = useModelWithIdentifier({
    identifier: searchProduct.identifier,
    companyId: companyId as number,
  });

  const { associatedAbstractModels, isLoadingAssociatedAbstractModels } =
    useAssociatedAbstractModels({
      companyId: companyId as number,
      identifiers:
        modelData?.modelReverseAssociations || modelData?.productAssociations
          ? combineKeys(
              modelData.modelReverseAssociations,
              modelData.productAssociations,
            )
          : undefined,
    });

  useEffect(() => {
    if (associatedAbstractModels) {
      setTableData(associatedAbstractModels);
    }
  }, [associatedAbstractModels]);

  const createdBodyTemplate = (rowData: SearchProduct) => {
    return (
      <div style={{ whiteSpace: "nowrap" }}>
        {rowData.updated
          ? moment(rowData.updated).format("DD.MM.YYYY HH:mm")
          : " - "}
      </div>
    );
  };

  const nameBodyTemplate = (rowData: SearchProduct) => {
    return (
      <IdentifierLink
        role="link"
        tabIndex={0}
        style={{ width: "280px" }}
        onClick={() => handleSelectItem(rowData)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            handleSelectItem(rowData);
          }
        }}
      >
        {rowData.name}
      </IdentifierLink>
    );
  };

  const eanBodyTemplate = (rowData: SearchProduct) => {
    return (
      <IdentifierLink
        role="link"
        tabIndex={0}
        onClick={() => handleSelectItem(rowData)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            handleSelectItem(rowData);
          }
        }}
      >
        {rowData.ean}
      </IdentifierLink>
    );
  };

  const typeTemplate = (rowData: SearchProduct) => {
    return (
      <TypeTag type={rowData.family || null}>
        {rowData.family === "product_model"
          ? t("label-model")
          : rowData.family === "package"
            ? t("label-multipliable-package")
            : t("label-product")}
      </TypeTag>
    );
  };

  const allowExpansion = (rowData: SearchProduct) => {
    return rowData.family === Family.product_model;
  };

  const rowExpansionTemplate = (data: SearchProduct) => {
    return (
      <SearchProductExpanded
        searchProduct={data}
        handleSelectItem={handleSelectItem}
      />
    );
  };

  return (
    <ExpandedContainer>
      <LoadingContainer
        status={
          isLoadingModel || isLoadingAssociatedAbstractModels
            ? HttpRequestStatus.LOADING
            : HttpRequestStatus.SUCCESS
        }
      >
        <StyledTable
          value={tableData}
          expandedRows={expandedRows}
          onRowToggle={(e) => setExpandedRows(e.data)}
          rowExpansionTemplate={rowExpansionTemplate}
          emptyMessage={t("label-no-associated-abstract-models")}
        >
          <Column style={{ width: "65px" }} expander={allowExpansion} />
          <Column field="type" body={typeTemplate} style={{ width: "150px" }} />
          <Column
            field="name"
            body={nameBodyTemplate}
            style={{ minWidth: "335px", width: "335px" }}
          />
          <Column
            field="ean"
            body={eanBodyTemplate}
            style={{ minWidth: "175px", width: "175px" }}
          />
          <Column
            field="internalReference"
            style={{ minWidth: "175px", width: "175px" }}
          />
          <Column field="created" body={createdBodyTemplate} />
          <Column field="placeholder" style={{ width: "160px" }} />
        </StyledTable>
      </LoadingContainer>
    </ExpandedContainer>
  );
};

export default SearchProductExpanded;
